import { type ReactNode, useMemo } from 'react';

import type { ApiISODateTimeString } from 'api/types/ApiTypedDate';
import { useUser } from 'contexts/UserEnvironmentContext';
import { useHover } from 'hooks/useHover';
import type { SearchAlgoliaJobHit } from 'modules/search/algolia/types/SearchAlgoliaJobHit';
import { SearchHitImage } from 'modules/search/components/Hit/Image/SearchHitImage';
import { SearchHitOwnerOrgBanner } from 'modules/search/components/Hit/OrgOwnerBanner/SearchHitOwnerOrgBanner';
import { SearchHitSaveButtonContainer } from 'modules/search/components/Hit/SaveButton/SearchHitSaveButtonContainer';
import {
  SearchHitCard,
  SearchHitImageHolder,
  SearchHitSaveButtonHolder,
} from 'modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from 'modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import type { SearchContextHeaderLocationState } from 'modules/search/containers/SearchContextHeader/SearchContextHeaderLocationState';
import { getWorkLocationString } from 'utils/address/getWorkLocationString';
import { formatSalaryRangeWithPeriodHumanName } from 'utils/listings';

import { SearchJobHitHolder } from './SearchJobHit.styled';
import { SearchJobHitInfo } from './SearchJobHitInfo';
import { useSearchJobHitEarlyApplicantLabel } from './useSearchJobHitEarlyApplicantLabel';

type Props = {
  hit: SearchAlgoliaJobHit;
  href: string;
  trackClick?: () => void;
  showLabel?: boolean;
  hideCta?: boolean;
  hideOwnerBanner?: boolean;
  highlighted?: boolean;
  application?: {
    status: 'ACTIVE' | 'INACTIVE';
    created: ApiISODateTimeString;
  };
  actions?: ReactNode;
};

export function SearchJobHit({
  hit,
  href,
  trackClick,
  showLabel,
  hideCta,
  hideOwnerBanner,
  highlighted,
  application,
  actions,
}: Props) {
  const { user } = useUser();

  const {
    objectID,
    name,
    logo,
    city,
    state,
    country,
    published,
    orgName,
    orgType,
    orgID,
    jobType,
    locationType,
    remoteZone,
    isPostedAnonymously,
    salaryMinimum,
    salaryMaximum,
    salaryPeriod,
    salaryCurrency,
    earlyApplicant,
  } = hit;

  const location = useMemo(
    () =>
      getWorkLocationString({
        remoteOptions: {
          remoteZone,
          remoteCountry: country,
          remoteState: state,
        },
        address: {
          city,
          country,
          stateCode: state,
        },
      }),
    [remoteZone, city, country, state],
  );

  const showSaveButton = !hideCta;

  const ownerOrg = user ? user.orgs.find((o) => o.id === orgID) : null;

  const salaryFormatted = useMemo(
    () =>
      salaryPeriod && (salaryMinimum || salaryMaximum)
        ? formatSalaryRangeWithPeriodHumanName(
            salaryMinimum ? parseFloat(salaryMinimum) : null,
            salaryMaximum ? parseFloat(salaryMaximum) : null,
            country,
            salaryCurrency,
            salaryPeriod,
          )
        : '',
    [salaryMinimum, salaryMaximum, country, salaryCurrency, salaryPeriod],
  );

  const [ref, listingIsHovered] = useHover();
  const { earlyApplicantVisibility } = useSearchJobHitEarlyApplicantLabel({
    earlyApplicant,
    listingHighlighted: Boolean(highlighted),
    listingIsHovered,
  });

  return (
    <SearchJobHitHolder
      ref={ref}
      data-qa-id="search-result"
      data-hit-id={objectID}
      $earlyApplicantVisibility={earlyApplicantVisibility}
    >
      {!hideOwnerBanner && ownerOrg && (
        <SearchHitOwnerOrgBanner org={ownerOrg} hit={hit} />
      )}

      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          actions={actions}
          roundedTop={hideOwnerBanner || !ownerOrg}
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link-with-state',
            to: href,
            state: {
              showSearchContext: true,
            } satisfies SearchContextHeaderLocationState,
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitImageHolder $hiddenOnMobile={!logo}>
            <SearchHitImage
              isImported={false}
              imageUrl={undefined}
              image={undefined}
              source={undefined}
              name={name}
              logo={logo || undefined}
            />
          </SearchHitImageHolder>

          <SearchJobHitInfo
            application={application}
            jobType={jobType}
            isPostedAnonymously={isPostedAnonymously}
            location={location}
            locationType={locationType}
            name={name}
            orgName={orgName}
            orgType={orgType}
            salaryFormatted={salaryFormatted}
            showLabel={showLabel}
            published={published}
            earlyApplicant={Boolean(earlyApplicantVisibility)}
          />
        </SearchHitInteractiveContentWrapper>

        {showSaveButton && (
          <SearchHitSaveButtonHolder>
            <SearchHitSaveButtonContainer hit={hit} />
          </SearchHitSaveButtonHolder>
        )}
      </SearchHitCard>
    </SearchJobHitHolder>
  );
}
